import { Drawer, Spin } from 'antd';
import { LoadingOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

import { useAppSelector } from 'hooks';
import { BillTypes, Cart, CurrencySymbol, ShopType } from 'models';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSelectedProductOptions, moneyFormat } from 'utils';
import * as svg from '../../../constants/ui/svg';

interface IModalCart {
  open: boolean;
  setOpen: (open: boolean) => void;
  cart: Cart[];
  setCart: (cart: Cart[]) => void;
  onDecrease: (item: Cart) => void;
  onIncrease: (item: Cart) => void;
  addProductInBill: () => void;
  loadingCart: boolean;
  setLoadingCart: (loadingCart: boolean) => void;
}

const useScreenSize = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const ModalCart: FC<IModalCart> = ({
  open,
  setOpen,
  cart,
  onDecrease,
  onIncrease,
  addProductInBill,
  loadingCart,
  setLoadingCart,
}) => {
  const isMobile = useScreenSize();
  const { t } = useTranslation();
  const billInfo = useAppSelector((state) => state.bill.billInfo) as BillTypes;
  const shopInfo = useAppSelector((state) => state.shop.shopInfo) as ShopType;
  const getMainHost = useMemo(() => {
    const names = billInfo?.service_fee
      ?.filter((item) => item.type === 1)
      .map((item) => item?.user_of_shop?.nick_name)
      .filter(Boolean)
      .join(', ');
    return names && names.length > 0 ? names : ' - ';
  }, [billInfo]);
  const renderItemCart = (item: Cart, index: number) => {
    const getTotalPrice = () => {
      const optionPrice = getSelectedProductOptions(
        item.product_options || [],
        item.selected_options,
        'price'
      );
      return (item.price + optionPrice) * item.quantity;
    };
    const convertNameOption = () => {
      if (!item?.selected_options || !Array.isArray(item.selected_options)) {
        return '';
      }

      const productOptions = item?.product_options?.[0]?.options;
      if (!productOptions || !Array.isArray(productOptions)) {
        return '';
      }

      const names = item.selected_options
        .map((itemOption) => {
          if (!itemOption || typeof itemOption.option_id === 'undefined') {
            return null;
          }

          const option = productOptions?.find((opt) => opt.id === itemOption.option_id);
          return option?.name || null;
        })
        .filter((name) => name !== null)
        .join(', ');

      return names || '';
    };

    // console.log(convertNameOption(), item);
    return (
      <div className="w-full flex flex-col" key={item.id ?? index}>
        <div className="px-2 justify-between flex items-center text-sm mb-2 border-b border-#D0D5DD py-2">
          <div className="flex flex-1 items-center gap-x-2">
            <span>{item.quantity}x</span>
            <span>
              {item.name} {convertNameOption() ? `(${convertNameOption()})` : ''}
            </span>
          </div>
          <div className="flex flex-1 items-center gap-x-2">
            <button
              className="bg-primary text-sm text-white w-[40px] h-[40px] rounded-full flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDecrease(item);
              }}
            >
              {svg.minusIcon}
            </button>
            <button
              className="bg-primary text-sm text-white w-[40px] h-[40px] rounded-full flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onIncrease(item);
              }}
            >
              {svg.plusIcon}
            </button>
          </div>
          <div className="flex items-center gap-x-2">
            <span>{moneyFormat(getTotalPrice(), shopInfo?.currency as CurrencySymbol)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Not used, but kept for future use
  // const stayTimeOfUnpaidBill = () => {
  //   const diff = moment(billInfo.stayTime).diff(moment(billInfo?.created_at), 'minutes');
  //   let hour = Math.floor(diff / 60);
  //   let minute = diff % 60;
  //   if (diff < 0) {
  //     hour += 23;
  //     minute += 60;
  //   }
  //   return `${hour}${t('HOUR_MINI')}${minute}${t('MINUTE_MINI')}`;
  // };

  return (
    <Drawer
      open={open}
      onClose={() => setOpen(false)}
      placement="bottom"
      height={isMobile ? '80%' : '80%'}
      width={isMobile ? '100%' : 600}
      headerStyle={{
        borderBottom: '1px solid #f0f0f0',
        padding: '10px 12px 10px 0px',
      }}
      bodyStyle={{ flex: 1, display: 'flex', padding: 0 }}
      closeIcon={<></>}
      title={
        <div className="flex items-center justify-between p-0">
          <span className="text-brown_dark text-xl font-noto-sans-bold font-bold">
            {t('ORDER_CONFIRMATION')}
          </span>
          <button
            onClick={() => setOpen(false)}
            className="text-primary font-noto-sans-medium font-medium"
          >
            {t('CLOSE')}
          </button>
        </div>
      }
    >
      <div className="flex flex-1 flex-col">
        {!shopInfo?.food && (
          <div className="flex flex-col border-t border-border_primary">
            <div className="flex items-center text-sm mb-2 border-b border-#D0D5DD py-2">
              <span className="flex-1 border-r border-#D0D5DD px-2">
                {t('TABLE')}: {billInfo?.tables?.name || t('N/A')}
              </span>
              <span className="flex-1 border-r border-#D0D5DD px-2">
                {t('NUMBER_OF_CUSTOMER')}: {`${billInfo?.number_customer ?? 0} ${t('PEOPLES')}`}
              </span>
              <span className="flex-1 px-2">
                {t('HOST_NAME')}: {getMainHost}
              </span>
            </div>
          </div>
        )}
        <div className="flex flex-1 flex-col">{cart.map(renderItemCart)}</div>
        <div className="px-3 py-4 border-t border-#D0D5DD">
          <button
            disabled={loadingCart}
            onClick={addProductInBill}
            className="w-full bg-primary text-white rounded-lg font-noto-sans-medium font-medium text-lg py-3"
          >
            {loadingCart ? (
              <Spin
                indicator={
                  <LoadingOutlined style={{ fontSize: 20, color: 'white', marginRight: 10 }} spin />
                }
              />
            ) : (
              t('ORDER')
            )}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default memo(ModalCart);
